import { Button, Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle, Spinner } from '@lensora/ui'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import * as React from 'react'

import { useActiveWebsite } from '@/hooks/use-active-website'
import { api, ApiError } from '@/lib/api'

interface Ga4Status {
  configured: boolean
  connected: boolean
  property_id: string | null
  property_display_name: string | null
  connected_at: string | null
}

// Reads the ga4_success / ga4_error params the OAuth callback redirects back
// with, surfaces a one-time message, then strips them from the URL so a
// refresh doesn't re-show it.
function useGa4CallbackResult() {
  const [result, setResult] = React.useState<{ type: 'success' | 'error'; message: string } | null>(null)

  React.useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    const error = params.get('ga4_error')
    const success = params.get('ga4_success')
    if (!error && !success) return

    setResult(
      error
        ? { type: 'error', message: GA4_ERROR_MESSAGES[error] ?? 'Failed to connect Google Analytics.' }
        : { type: 'success', message: 'Google Analytics connected.' }
    )

    params.delete('ga4_error')
    params.delete('ga4_success')
    const next = `${window.location.pathname}${params.toString() ? `?${params.toString()}` : ''}`
    window.history.replaceState({}, '', next)
  }, [])

  return result
}

const GA4_ERROR_MESSAGES: Record<string, string> = {
  invalid_state: 'That connection link expired — please try again.',
  website_not_found: 'Website not found.',
  no_property_found: 'No Google Analytics 4 property found on that account.',
  property_ambiguous:
    "Found multiple Google Analytics properties and couldn't tell which belongs to this website. Contact support to connect it manually.",
  connection_failed: 'Something went wrong connecting Google Analytics.',
}

export function GA4IntegrationCard() {
  const queryClient = useQueryClient()
  const { websiteId } = useActiveWebsite()
  const callbackResult = useGa4CallbackResult()
  const [connecting, setConnecting] = React.useState(false)
  const [connectError, setConnectError] = React.useState<string | null>(null)

  const { data, isLoading } = useQuery({
    queryKey: ['ga4-status', websiteId],
    queryFn: () => api.get<Ga4Status>(`/api/ga4/status?website_id=${websiteId}`),
    enabled: !!websiteId,
  })

  const disconnect = useMutation({
    mutationFn: () => api.post('/api/ga4/disconnect', { website_id: websiteId }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['ga4-status', websiteId] }),
  })

  async function connect() {
    if (!websiteId) return
    setConnecting(true)
    setConnectError(null)
    try {
      const returnUrl = window.location.href.split('?')[0] ?? window.location.href
      const { url } = await api.get<{ url: string }>(
        `/api/ga4/connect-url?website_id=${websiteId}&return_url=${encodeURIComponent(returnUrl)}`
      )
      window.location.href = url
    } catch (err) {
      setConnectError(err instanceof ApiError ? err.message : 'Failed to start Google Analytics connection.')
      setConnecting(false)
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Google Analytics</CardTitle>
        <CardDescription>
          Connect GA4 to see how much of your traffic comes from AI assistants like ChatGPT and Perplexity.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        {callbackResult && (
          <p className={`text-sm ${callbackResult.type === 'error' ? 'text-feedback-error' : 'text-feedback-success'}`}>
            {callbackResult.message}
          </p>
        )}

        {isLoading ? (
          <div className="flex items-center gap-2 text-sm text-text-muted">
            <Spinner size="sm" /> Checking connection…
          </div>
        ) : !data?.configured ? (
          <p className="text-sm text-text-muted">
            This integration isn't set up for your account yet — check back soon.
          </p>
        ) : data.connected ? (
          <div className="text-sm text-text">
            <p>
              Connected to <span className="font-medium">{data.property_display_name ?? data.property_id}</span>.
            </p>
            <p className="mt-1 text-text-muted">
              View the traffic breakdown on the{' '}
              <a href="/dashboard/analytics" className="text-brand-accent hover:underline">
                Analytics
              </a>{' '}
              page.
            </p>
          </div>
        ) : (
          <p className="text-sm text-text-muted">Not connected yet.</p>
        )}

        {connectError && <p className="text-sm text-feedback-error">{connectError}</p>}
      </CardContent>
      <CardFooter>
        {data?.configured && !isLoading && (
          data.connected ? (
            <Button
              variant="outline"
              size="sm"
              onClick={() => disconnect.mutate()}
              disabled={disconnect.isPending}
            >
              {disconnect.isPending ? 'Disconnecting…' : 'Disconnect'}
            </Button>
          ) : (
            <Button size="sm" onClick={() => void connect()} disabled={connecting || !websiteId}>
              {connecting ? 'Redirecting…' : 'Connect Google Analytics'}
            </Button>
          )
        )}
      </CardFooter>
    </Card>
  )
}
